<template>
  <div
    v-if="isOpen"
    class="modal fade show d-block bg-dark bg-opacity-50"
    @click.self="closeModal"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{
              isCreateMode
                ? "Create Registry Connection"
                : "Edit Registry Connection"
            }}
          </h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <div class="modal-body">
          <form @submit.prevent="handleSubmit">
            <!-- Integration Name -->
            <div class="mb-3">
              <label for="integration_name" class="form-label"
                >Registry Integration
                <span v-if="isCreateMode" class="text-danger">*</span></label
              >
              <select
                v-if="isCreateMode"
                id="integration_name"
                v-model="formData.integration_name"
                class="form-select"
                :class="{ 'is-invalid': v$.integration_name.$error }"
                @blur="v$.integration_name.$touch()"
              >
                <option value="">Select an integration...</option>
                <option
                  v-for="integration in AVAILABLE_INTEGRATIONS"
                  :key="integration"
                  :value="integration"
                >
                  {{ formatIntegrationName(integration) }}
                </option>
              </select>
              <input
                v-else
                type="text"
                class="form-control"
                :value="formatIntegrationName(formData.integration_name)"
                readonly
                disabled
              />
              <div v-if="v$.integration_name.$error" class="invalid-feedback">
                {{ v$.integration_name.$errors[0].$message }}
              </div>
              <small v-if="!isCreateMode" class="form-text text-muted"
                >Integration type cannot be changed</small
              >
            </div>

            <div v-if="!isCreateMode" class="mb-3">
              <label :for="regionId" class="form-label">Region</label>
              <select
                v-if="regionOptions.length > 1"
                :id="regionId"
                v-model="formData.region"
                class="form-select"
              >
                <option
                  v-for="region in regionOptions"
                  :key="region"
                  :value="region"
                >
                  {{ region }}
                </option>
              </select>
              <input
                v-else
                :id="regionId"
                type="text"
                class="form-control"
                :value="formData.region"
                readonly
                disabled
              />
              <small
                v-if="regionOptions.length > 1"
                class="form-text text-muted"
              >
                The registry country the account lives in. Set from the
                company's country when the connection was created; correct it if
                the account is held in another country's registry.
              </small>
              <small v-else class="form-text text-muted"
                >Region cannot be changed</small
              >
            </div>

            <!-- Registry Company ID (Editable) -->
            <div class="mb-3">
              <label for="registry_company_id" class="form-label"
                >Registry Company ID</label
              >
              <input
                id="registry_company_id"
                v-model.trim="formData.registry_company_id"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v$.registry_company_id.$error }"
                placeholder="Enter company ID from registry system"
                @blur="v$.registry_company_id.$touch()"
              />
              <div
                v-if="v$.registry_company_id.$error"
                class="invalid-feedback"
              >
                {{ v$.registry_company_id.$errors[0].$message }}
              </div>
              <small class="form-text text-muted">
                Your company identifier in the
                {{ formatIntegrationName(formData.integration_name) }} registry
                system
              </small>
            </div>

            <!-- Registry EECS (Editable) -->
            <div class="mb-3">
              <label for="registry_eecs" class="form-label"
                >Registry EECS Code</label
              >
              <input
                id="registry_eecs"
                v-model.trim="formData.registry_eecs"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': v$.registry_eecs.$error }"
                placeholder="Enter EECS code if applicable"
                @blur="v$.registry_eecs.$touch()"
              />
              <div v-if="v$.registry_eecs.$error" class="invalid-feedback">
                {{ v$.registry_eecs.$errors[0].$message }}
              </div>
              <small class="form-text text-muted">
                European Energy Certificate System code (optional)
              </small>
            </div>

            <div v-if="isCnmc" class="mb-3 form-check form-switch">
              <input
                :id="spanishIssuanceId"
                v-model="formData.spanish_issuance_enabled"
                type="checkbox"
                class="form-check-input"
                :aria-describedby="`${spanishIssuanceId}-help`"
              />
              <label :for="spanishIssuanceId" class="form-check-label">
                Enable Spanish issuance
              </label>
              <div :id="`${spanishIssuanceId}-help`" class="form-text">
                Enables issuance and transfer/export preparation for this CNMC
                producer. Requires a Spanish NIF and the global issuance switch.
                Turning this off keeps existing filings and monitoring
                available.
              </div>
            </div>

            <!-- Known redemption registros (CNMC only) -->
            <div v-if="isCnmc" class="mb-3">
              <label for="known_redemption_registros" class="form-label"
                >Known redemption registros</label
              >
              <textarea
                id="known_redemption_registros"
                v-model="formData.known_redemption_registros"
                class="form-control"
                rows="3"
                placeholder="One Nº Registro per line, e.g. 20260300000043708271"
              ></textarea>
              <small class="form-text text-muted">
                Redemption trámite registro numbers from the client's Sede
                receipts — the only way their redemptions become visible in the
                reconstructed holdings. Add new numbers on their own lines.
              </small>
            </div>

            <!-- Eligible for Issuance Fee (Edit mode only) -->
            <div v-if="!isCreateMode" class="mb-3 form-check">
              <input
                id="eligible_for_issuance_fee"
                v-model="formData.eligible_for_issuance_fee"
                type="checkbox"
                class="form-check-input"
              />
              <label for="eligible_for_issuance_fee" class="form-check-label"
                >Eligible for issuance fee</label
              >
              <div>
                <small class="form-text text-muted">
                  Enable if certificates are issued to Soldera's registry
                  account (device transfer) and the company should be billed for
                  issuance fees
                </small>
              </div>
            </div>

            <!-- Eligible for Soldera Issuance Fee (Edit mode only) -->
            <div v-if="!isCreateMode" class="mb-3 form-check">
              <input
                id="eligible_for_soldera_issuance_fee"
                v-model="formData.eligible_for_soldera_issuance_fee"
                type="checkbox"
                class="form-check-input"
              />
              <label
                for="eligible_for_soldera_issuance_fee"
                class="form-check-label"
                >Eligible for Soldera issuance fee</label
              >
              <div>
                <small class="form-text text-muted">
                  Enable if the company should be billed for Soldera's own
                  issuance fees
                </small>
              </div>
            </div>

            <div v-if="canDisableTransactionLog" class="mb-3 form-check">
              <input
                id="transaction_log_disabled"
                v-model="formData.transaction_log_disabled"
                type="checkbox"
                class="form-check-input"
              />
              <label for="transaction_log_disabled" class="form-check-label"
                >Disable transaction log</label
              >
              <div>
                <small class="form-text text-muted">
                  Stops transaction log ingestion and incoming-transfer routing
                  for this connection on the next sync
                </small>
              </div>
            </div>
            <div v-else-if="transactionLogExclusion" class="mb-3">
              <span class="form-label d-block">Transaction log</span>
              <small class="form-text text-muted">
                {{ transactionLogExclusion }}
              </small>
            </div>

            <div v-if="!isCreateMode" class="mb-3">
              <label for="sync_tier" class="form-label">Sync cadence</label>
              <select
                id="sync_tier"
                v-model="formData.sync_tier"
                class="form-select"
              >
                <option value="NIGHTLY">Nightly</option>
                <option value="FREQUENT">Frequent (every 15 minutes)</option>
              </select>
              <small class="form-text text-muted">
                A connection is synced by exactly one cadence. Frequent runs the
                full sync, including Grexel issuance approval, on every tick.
              </small>
            </div>
          </form>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="handleSubmit"
            :disabled="loading || v$.$invalid"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            {{ isCreateMode ? "Create Connection" : "Save Changes" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { union } from "lodash-es";
import { reactive, ref, watch, computed, useId } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { useValidators } from "@/composables/useValidators";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useI18n } from "vue-i18n";
import type { RegistryConnection, RegistrySyncTier } from "@/types";
import {
  AVAILABLE_INTEGRATIONS,
  formatIntegrationName,
  transactionLogOffReason,
} from "@/registryUtils";

interface Props {
  connection: RegistryConnection | null;
  companyId: number;
  mode?: "create" | "edit";
}

interface Emits {
  "connection-updated": [connection: RegistryConnection];
  "connection-created": [connection: RegistryConnection];
}

const props = withDefaults(defineProps<Props>(), {
  mode: "edit",
});
const emit = defineEmits<Emits>();
const isOpen = defineModel<boolean>({ required: true });

const { t } = useI18n();
const { maxLength, required } = useValidators(t);
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

const isCreateMode = computed(() => props.mode === "create");
const spanishIssuanceId = useId();
const regionId = useId();

const formData = reactive({
  integration_name: "",
  registry_company_id: "",
  registry_eecs: "",
  region: "",
  eligible_for_issuance_fee: false,
  eligible_for_soldera_issuance_fee: false,
  transaction_log_disabled: false,
  sync_tier: "NIGHTLY" as RegistrySyncTier,
  known_redemption_registros: "",
  spanish_issuance_enabled: false,
});

const isCnmc = computed(() => formData.integration_name === "CNMC");
const canDisableTransactionLog = computed(
  () =>
    !isCreateMode.value &&
    props.connection?.transaction_log_rule_exclusion === null
);
const transactionLogExclusion = computed(() =>
  !isCreateMode.value && props.connection
    ? transactionLogOffReason(props.connection)
    : null
);

// The saved region stays selectable even when the registry doesn't serve it, so the select never shows it blank
const regionOptions = computed(() =>
  union(props.connection?.region_options ?? [], [formData.region])
);

const parseRedemptionRegistros = (): string[] =>
  formData.known_redemption_registros
    .split("\n")
    .map((registro) => registro.trim())
    .filter((registro) => registro.length > 0);

const loading = ref(false);

const validationRules = computed(() => ({
  integration_name: isCreateMode.value ? { required } : {},
  registry_company_id: { maxLength: maxLength(258) },
  registry_eecs: { maxLength: maxLength(258) },
}));

const v$ = useVuelidate(validationRules, formData);

watch(
  [() => props.connection, isOpen, () => props.mode],
  ([connection, open, mode]) => {
    if (open) {
      if (mode === "edit" && connection) {
        // Edit mode: populate from connection
        formData.integration_name = connection.integration_name;
        formData.registry_company_id = connection.registry_company_id || "";
        formData.registry_eecs = connection.registry_eecs || "";
        formData.region = connection.region;
        formData.spanish_issuance_enabled =
          connection.spanish_issuance_enabled ?? false;
        formData.eligible_for_issuance_fee =
          connection.eligible_for_issuance_fee;
        formData.eligible_for_soldera_issuance_fee =
          connection.eligible_for_soldera_issuance_fee;
        formData.transaction_log_disabled = connection.transaction_log_disabled;
        formData.sync_tier = connection.sync_tier;
        formData.known_redemption_registros = (
          connection.known_redemption_registros || []
        ).join("\n");
      } else if (mode === "create") {
        // Create mode: reset form
        formData.integration_name = "";
        formData.registry_company_id = "";
        formData.registry_eecs = "";
        formData.region = "";
        formData.spanish_issuance_enabled = false;
        formData.eligible_for_issuance_fee = false;
        formData.eligible_for_soldera_issuance_fee = false;
        formData.transaction_log_disabled = false;
        formData.sync_tier = "NIGHTLY";
        formData.known_redemption_registros = "";
      }
      // Reset validation when initializing form
      v$.value.$reset();
    }
  },
  { immediate: true }
);

const closeModal = () => {
  isOpen.value = false;
};

const handleSubmit = async () => {
  // Validate form
  await v$.value.$validate();

  if (v$.value.$invalid) {
    return;
  }

  if (isCreateMode.value) {
    await handleCreate();
  } else {
    await handleUpdate();
  }
};

const handleCreate = async () => {
  loading.value = true;

  try {
    const response = await goSolidApi.post(
      `/api/admin/companies/${props.companyId}/registry-connections`,
      {
        integration_name: formData.integration_name,
        registry_company_id: formData.registry_company_id || null,
        registry_eecs: formData.registry_eecs || null,
        spanish_issuance_enabled: isCnmc.value
          ? formData.spanish_issuance_enabled
          : null,
        known_redemption_registros: isCnmc.value
          ? parseRedemptionRegistros()
          : null,
      }
    );

    if (response.status === 201) {
      emit("connection-created", response.data.data);
      globalSuccessHandler(
        "Success",
        "Registry connection created successfully"
      );
      closeModal();
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    loading.value = false;
  }
};

const handleUpdate = async () => {
  if (!props.connection) {
    return;
  }

  loading.value = true;

  try {
    const response = await goSolidApi.put(
      `/api/admin/companies/${props.companyId}/registry-connections/${props.connection.id}`,
      {
        registry_company_id: formData.registry_company_id || null,
        registry_eecs: formData.registry_eecs || null,
        integration_name: formData.integration_name,
        region:
          formData.region === props.connection.region ? null : formData.region,
        spanish_issuance_enabled: isCnmc.value
          ? formData.spanish_issuance_enabled
          : null,
        eligible_for_issuance_fee: formData.eligible_for_issuance_fee,
        eligible_for_soldera_issuance_fee:
          formData.eligible_for_soldera_issuance_fee,
        transaction_log_disabled: canDisableTransactionLog.value
          ? formData.transaction_log_disabled
          : null,
        sync_tier: formData.sync_tier,
        known_redemption_registros: isCnmc.value
          ? parseRedemptionRegistros()
          : null,
      }
    );

    if (response.status === 200) {
      emit("connection-updated", response.data.data);
      globalSuccessHandler(
        "Success",
        "Registry connection updated successfully"
      );
      closeModal();
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    loading.value = false;
  }
};
</script>
